import { useSDKConfigStore } from "../stores/sdkConfigStore";
import {
  getCreditDelegationTxData,
  createCard,
  getUserCards,
  updateCard,
} from "../api/api";
import { dataFetcher } from "./dataFetcher";
import { useCardStatusStore } from "../stores/cardStatusStore";
import { UserCard } from "../api/types";
import { generateCardOperationSign } from "../api/auth";

interface DelegateCardParams {
  chainID: number;
  creditType: "aave-v3" | "euler" | "hypurrFi";
  creditLimit: number;
  sendTransactionAsync: (params: {
    to: string;
    value: bigint;
    data: string;
  }) => Promise<string>; // Returns transaction hash
  signTypedData: (typedData: any) => Promise<string>;
  userParams?:
    | {
        vaults: string[];
        borrowVaultAddress: string;
      }
    | {
        borrowTokenAddress: string;
      };
}

export async function delegateCard({
  chainID,
  creditLimit,
  creditType,
  sendTransactionAsync,
  signTypedData,
  userParams,
}: DelegateCardParams): Promise<{ success: boolean; message: string }> {
  const { eoaAddress } = useSDKConfigStore.getState();
  const { userCard } = useCardStatusStore.getState();

  if (!eoaAddress) {
    return {
      success: false,
      message: "EOA address not found. Please initialize the SDK first.",
    };
  }

  if (creditLimit <= 0) {
    return {
      success: false,
      message: "Credit limit must be greater than 0.",
    };
  }

  try {
    console.log("[delegateCard] Step 1: Fetching credit delegation tx data...");

    const delegationResponse = await getCreditDelegationTxData({
      from: eoaAddress,
      creditLimit,
      creditType,
      userParams,
    });

    const delegation = delegationResponse.data;
    if (!delegation.to || !delegation.callData) {
      return {
        success: false,
        message: "Invalid transaction data received from delegation service.",
      };
    }

    console.log("[delegateCard] Step 2: Sending delegation transaction...");
    const transactionHash = await sendTransactionAsync({
      to: delegation.to,
      value: BigInt(delegation.value || 0),
      data: delegation.callData,
    });
    console.log("[delegateCard] Transaction sent:", transactionHash);

    let card: UserCard | null = userCard;

    // Step 3: Create card if not already present
    if (!card) {
      console.log("[delegateCard] Creating card...");

      const eoa = eoaAddress as `0x${string}`;
      const cardID = "00000000-0000-0000-0000-000000000000"; // Used for signature as this is a new card
      const timestamp = Math.floor(Date.now() / 1000);
      const action = "createCard";

      console.log("[delegateCard] Signing authorization for card creation...");
      const { signature } = await generateCardOperationSign(
        eoa,
        chainID,
        cardID,
        action,
        timestamp,
        signTypedData
      );

      const authJson = {
        cardID,
        action,
        version: "1",
        timestamp,
        chainID,
        sig: signature,
        userAddr: eoa,
      };

      const encodedAuth = btoa(JSON.stringify(authJson));

      const createCardResponse = await createCard(
        eoaAddress,
        chainID,
        creditType,
        encodedAuth
      );
      card = createCardResponse.data;

      if (!card?.cardID) {
        return {
          success: false,
          message: "Failed to create card - invalid card ID received.",
        };
      }
    }

    const eoa = eoaAddress as `0x${string}`;
    const cardID = card.cardID;
    const timestamp = Math.floor(Date.now() / 1000);
    const action = "updateCardCreditProviderAddress";

    console.log("[delegateCard] Step 4: Signing authorization...");
    const { signature } = await generateCardOperationSign(
      eoa,
      chainID,
      cardID,
      action,
      timestamp,
      signTypedData
    );

    const authJson = {
      cardID,
      action,
      version: "1",
      timestamp,
      chainID,
      sig: signature,
      userAddr: eoa,
    };

    const encodedAuth = btoa(JSON.stringify(authJson));

    console.log("[delegateCard] Step 5: Linking card to credit provider...");
    await updateCard({
      creditProviderAddress: eoaAddress,
      chainId: chainID,
      creditType,
      auth: encodedAuth,
    });

    // Step 6: Verification of delegation
    console.log("[delegateCard] Step 6: Verifying delegation result...");
    const userCardsResponse = await getUserCards(eoaAddress);
    const updatedCard = userCardsResponse.data?.find(
      (c) => c.cardID === card.cardID
    );

    if (!updatedCard) {
      return {
        success: false,
        message:
          "Card not found after delegation - delegation may have failed.",
      };
    }

    if (
      updatedCard.creditProviderAddress.toLowerCase() !==
      eoaAddress.toLowerCase()
    ) {
      return {
        success: false,
        message:
          "Delegation failed - credit provider address does not match EOA address.",
      };
    }

    // Step 7: Final refetch
    console.log("[delegateCard] Step 7: Refetching updated data...");
    await dataFetcher.fetchDataAfterAuth();

    console.log("[delegateCard] Delegation completed successfully.");
    return {
      success: true,
      message: "Card delegated and linked successfully.",
    };
  } catch (err) {
    const message =
      err instanceof Error ? err.message : "An unexpected error occurred.";
    console.error("[delegateCard] Error during delegation:", err);
    return {
      success: false,
      message,
    };
  }
}
